import { CATEGORY_CITYWIDE_SEARCH } from 'utils/analytics/eventTypesConstants';
import { FilterButton, Tag } from 'omakase';
import { IconAdjust } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { IconRefine } from 'ui/icons';
import { LocalDate, LocalTime } from 'js-joda';
import { LocaleContext } from '@tock/coretypes';
import { PatronLocationContainer } from 'components/patron-location/PatronLocationContainer';
import { PatronLocationDesktopSearchResultsButton } from 'components/patron-location/PatronLocationDesktopSearchResultsButton';
import { SearchChangeProps } from 'components/www/availability-search/SearchHeader';
import {
  SearchResultsOpenMode,
  useIsDateQuickFilterEnabled,
  useVisibleFilters,
} from 'components/search/utils';
import { getAppliedFiltersCountForVisibleFilters } from 'utils/search/searchFiltersUtils';
import { getDisplayedTimes } from 'utils/timeUtils';
import { useCuisineFilters } from 'hooks/search/useCuisineFilters';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import CuisineFilterButton from 'components/www/availability-search/filters/CuisineFilterButton';
import DateTimeGuestsSearchBarButton from './DateTimeGuestsSearchBarButton';
import ExperienceTypeFilterButton from 'components/www/availability-search/filters/ExperienceTypeFilterButton';
import QuerySearchBarButton, {
  NO_SEARCH_UPDATES_SENTINEL,
  OnSearchType,
} from './QuerySearchBarButton';
import React, { FunctionComponent } from 'react';
import SearchFiltersDialog, {
  SearchFilterType,
} from 'components/www/availability-search/filters/SearchFiltersDialog';
import SearchFiltersDialogTrigger from 'components/www/availability-search/filters/SearchFiltersDialogTrigger';

// breaks in SSR when using an A/B experiment on a component
// const useStyles = makeStyles((theme: Theme) =>
//   createStyles({
//     root: {
//       backgroundColor: theme.colors.bgDefault,
//       borderTop: `1px solid ${theme.colors.borderDefault}`,
//       boxShadow: theme.shadow[100],
//       display: 'none',
//       maxHeight: '81px',
//       overflowX: 'auto',
//       overflowY: 'hidden',
//       padding: theme.spacing(2.25),
//       position: 'relative',
//       whiteSpace: 'nowrap',
//       '& > * + *': {
//         marginLeft: theme.spacing(1),
//       },
//       zIndex: theme.zIndex.appBar,

//       [theme.breakpoints.up('sm')]: {
//         display: 'flex',
//       },
//     },
//   })
// );

type SearchBarDesktopProps = Omit<
  SearchChangeProps,
  'header' | 'currentDate' | 'currentDateEnd' | 'currentTime' | 'currentSize' | 'currentType'
>;

export const CUISINE_FILTER_METROS = new Set([
  'Chicago',
  'New York City',
  'Los Angeles',
  'San Francisco',
]);

export const SearchBarDesktop: FunctionComponent<SearchBarDesktopProps> = ({
  date,
  dateEnd,
  q,
  size,
  time,
  type,
  collectionTagLabel,
  cuisines,
  currentQ,
  onChangeType,
  onChangeQ,
  onChangeSize,
  onChangeTime,
  onChangeDate,
  onChangeCuisines,
  onClearCollectionTag,
  onSearch,
  onReset,
  dateLabel,
  experiencesLabel,
  guestsLabel,
  qLabel,
  priceRange,
  experienceTags,
  onChangeSearchFilters,
  timeLabel,
  startDate,
  endDate,
  sizeOptions,
  typeOptions,
  showTimeFilterLabel,
  onChangeDateRange,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [openMode, setOpenMode] = React.useState<SearchResultsOpenMode>('CLOSED');
  const locale = React.useContext(LocaleContext);
  const { mode, availability } = { ...useSearchHandlerContext() };
  const visibleFilters = useVisibleFilters(mode, type);

  const { cuisineFiltersEnabled } = useCuisineFilters();

  const aggregations = availability?.result?.aggregations || undefined;

  const handleClickSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpenMode('SEARCH_OPEN');
  };

  const handleClickQ = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpenMode('TEXT_OPEN');
  };

  const handleClosePopover = () => {
    setOpenMode('CLOSED');
    onReset();
  };

  /**
   * if onSearch is called with no params, the current temp internal search state is sent
   */
  const handleSearch: OnSearchType = (params) => {
    setOpenMode('CLOSED');
    onSearch(params);
  };

  const showDate = visibleFilters.includes('DATE');
  const showTime = visibleFilters.includes('TIME') && !dateEnd;
  const showSize = visibleFilters.includes('SIZE');
  const showQ = visibleFilters.includes('TEXT') && currentQ && currentQ.length > 0;
  const showType = visibleFilters.includes('TYPE');
  const showTag = visibleFilters.includes('TAG');

  const displayedTimes = date
    ? getDisplayedTimes(LocalDate.parse(date), LocalTime.now(), locale)
    : [];

  const isMoreFiltersDialogOpen = () => openMode === 'SEARCH_FILTERS_OPEN';

  const visibleSearchFiltersDialogFilters: SearchFilterType[] = [
    'PRICE',
    'SEATING_AREA',
    'UNIQUE_EXPERIENCES',
  ];

  const appliedFiltersCount = getAppliedFiltersCountForVisibleFilters(
    visibleSearchFiltersDialogFilters,
    { priceRange, experienceTags, cuisines }
  );

  return (
    <div className="CitySearchHeader-toolbar" data-testid="search-label">
      {showType && (
        <ExperienceTypeFilterButton
          onOpen={(e: React.MouseEvent<HTMLButtonElement>) => {
            setOpenMode('TYPE_OPEN');
            setAnchorEl(e.currentTarget);
          }}
          anchorEl={anchorEl || undefined}
          experiencesLabel={experiencesLabel}
          isOpen={openMode === 'TYPE_OPEN'}
          onClose={() => setOpenMode('CLOSED')}
          onCloseAndReset={handleClosePopover}
          onChangeType={onChangeType}
          onSearch={onSearch}
          type={type}
          typeOptions={typeOptions}
        />
      )}

      <PatronLocationContainer initialComponent={PatronLocationDesktopSearchResultsButton} />

      {showQ && (
        <QuerySearchBarButton
          qLabel={qLabel}
          onClick={handleClickQ}
          anchorEl={anchorEl}
          onClose={() => setOpenMode('CLOSED')}
          isOpen={openMode === 'TEXT_OPEN'}
          q={q}
          onChangeQ={onChangeQ}
          onSearch={handleSearch}
        />
      )}
      <DateTimeGuestsSearchBarButton
        anchorEl={anchorEl || undefined}
        onClickSearch={handleClickSearch}
        showDate={showDate}
        showTime={showTime}
        showSize={showSize}
        dateLabel={dateLabel}
        timeLabel={timeLabel}
        guestsLabel={guestsLabel}
        isOpen={openMode === 'SEARCH_OPEN'}
        onClose={handleClosePopover}
        onChangeTime={onChangeTime}
        onChangeDate={onChangeDate}
        onChangeSize={onChangeSize}
        onSearch={() => handleSearch(NO_SEARCH_UPDATES_SENTINEL)}
        startDate={startDate}
        endDate={endDate}
        dateEnd={dateEnd}
        date={date}
        time={time}
        displayedTimes={displayedTimes}
        size={size}
        sizeOptions={sizeOptions}
        showTimeFilterLabel={showTimeFilterLabel}
        onChangeDateRange={onChangeDateRange}
        useDateRangePicker={useIsDateQuickFilterEnabled(type)}
      />

      {cuisineFiltersEnabled && (
        <CuisineFilterButton
          selectedCuisines={cuisines}
          onSubmit={onChangeCuisines}
          onOpen={(e: React.MouseEvent<HTMLButtonElement>) => {
            window.analytics.sendEvent(CATEGORY_CITYWIDE_SEARCH, 'Cuisine search selected');
            setOpenMode('CUISINES_OPEN');
            setAnchorEl(e.currentTarget);
          }}
          onClose={() => setOpenMode('CLOSED')}
          isOpen={openMode === 'CUISINES_OPEN'}
          anchorEl={anchorEl || undefined}
          aggregations={aggregations}
        />
      )}

      <SearchFiltersDialogTrigger
        onOpen={() => setOpenMode('SEARCH_FILTERS_OPEN')}
        triggerComponent={
          <FilterButton
            data-testid="desktop-search-filter-trigger"
            iconStart={<IconMigrationUtil legacyIcon={<IconRefine />} newIcon={<IconAdjust />} />}
          >
            More filters {appliedFiltersCount ? `· ${appliedFiltersCount}` : ''}
          </FilterButton>
        }
        appliedFiltersCount={appliedFiltersCount}
      />
      {showTag && (
        <Tag onDelete={onClearCollectionTag} variant="highContrast">
          {collectionTagLabel}
        </Tag>
      )}
      <SearchFiltersDialog
        onClose={() => setOpenMode('CLOSED')}
        isOpen={isMoreFiltersDialogOpen}
        dialogTitle="More filters"
        selectedType={type}
        selectedCuisines={cuisines}
        selectedPriceRange={priceRange}
        selectedExperienceTags={experienceTags}
        aggregations={aggregations}
        onSubmit={onChangeSearchFilters}
        visibleSearchFilters={visibleSearchFiltersDialogFilters}
      />
    </div>
  );
};
